/**
 * @description Composable for managing TanStack Query cache lifecycle
 * @author Omar Sánchez Martínez
 * @since 2025-10-15
 */

import { useQueryClient } from '@tanstack/vue-query';

/**
 * @description Hook for managing TanStack Query cache operations
 * Provides utilities to clear cache when switching accounts or logging out
 */
export const useQueryCacheManager = () => {
  const queryClient = useQueryClient();

  /**
   * @description Clears all TanStack Query cache
   * Should be called when logging out or switching accounts to prevent
   * showing data from previous sessions
   */
  const clearAllCache = (): void => {
    try {
      // Clear all queries from the cache
      queryClient.clear();

      // Additionally, remove all queries to ensure clean state
      queryClient.removeQueries();
    } catch (error) {
      console.error('[QueryCacheManager] Error clearing TanStack Query cache:', error);
    }
  };

  /**
   * @description Invalidates all queries, forcing them to refetch on next access
   * Useful when you want to refresh data but keep the cache structure
   */
  const invalidateAllQueries = async (): Promise<void> => {
    try {
      await queryClient.invalidateQueries();
      console.info('[QueryCacheManager] All queries invalidated successfully');
    } catch (error) {
      console.error('[QueryCacheManager] Error invalidating queries:', error);
    }
  };

  /**
   * @description Resets all queries to their initial state
   * This is more aggressive than invalidate and will clear loading/error states
   */
  const resetAllQueries = async (): Promise<void> => {
    try {
      await queryClient.resetQueries();
      console.info('[QueryCacheManager] All queries reset successfully');
    } catch (error) {
      console.error('[QueryCacheManager] Error resetting queries:', error);
    }
  };

  return {
    clearAllCache,
    invalidateAllQueries,
    resetAllQueries,
  };
};
